import { gql, useQuery } from '@apollo/client'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistMyCollectionsQuery,
  ArtistMyCollectionsQueryVariables,
  GetUserFolloweesQuery,
  GetUserFolloweesQueryVariables,
  UserCollectionBoxFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser, useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'

import { GET_USER_FOLLOWEES } from '../GlobalArtist/viewModel'
import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user: currentUser, userClaims } = useCurrentUser()
  const { analytics } = useAnalytics()
  const { router } = useRouter<{ artistId: string }>()
  const artistId = router.query.artistId ?? config.artistId

  const { data: userData } = useQuery<
    GetUserFolloweesQuery,
    GetUserFolloweesQueryVariables
  >(GET_USER_FOLLOWEES, {
    skip: !currentUser?.isSigned,
  })

  const { isFirstLoadRef } = useGetNewestData<
    ArtistMyCollectionsQuery,
    ArtistMyCollectionsQueryVariables
  >({
    query: QUERY,
    variables: {
      artistId,
      userId: userData?.user?.id ?? '',
      pageInput: { limit: 20 },
    },
    fields: [
      {
        fieldName: 'ownedUserCollectionsByArtistId',
        fragment: UserCollectionBoxFragmentDoc,
      },
    ],
  })

  const { data, loading, fetchMore } = useQuery<
    ArtistMyCollectionsQuery,
    ArtistMyCollectionsQueryVariables
  >(QUERY, {
    variables: {
      artistId,
      userId: userData?.user?.id ?? '',
      pageInput: { limit: 20 },
    },
    onCompleted({ ownedUserCollectionsByArtistId }) {
      isFirstLoadRef.current = false
      if (!ownedUserCollectionsByArtistId.edges.length) {
        analytics?.track('impression_noData', {
          applicationKey: config.applicationKey,
          name: 'artistMyCollections',
        })
      }
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  })
  const isNextFetch = !!data

  return {
    state: {
      loading: loading && !isNextFetch,
      artistId,
      hasNextPage: !!data?.ownedUserCollectionsByArtistId?.pageInfo.hasNextPage,
      userCollections:
        data?.ownedUserCollectionsByArtistId?.edges.filter(
          ({ node }) => node?.itemOwnedCount >= 1
        ) ?? null,
      user: userData?.user ?? null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      followArtists: userData?.user?.organizationFollowees ?? null,
      memberArtists: userData?.user?.organizationPatrons ?? null,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            pageInput: {
              cursor: data?.ownedUserCollectionsByArtistId?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ArtistMyCollections(
    $artistId: ID!
    $userId: String
    $pageInput: PageInput
  ) {
    ownedUserCollectionsByArtistId(
      artistId: $artistId
      pageInput: $pageInput
      userId: $userId
    ) {
      edges {
        node {
          ...UserCollectionBox
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
  ${UserCollectionBoxFragmentDoc}
`
